'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
// import dynamic from 'next/dynamic'; // Removed unused import
import { siteConfig } from '@/config/site'; // Import siteConfig

// Define the structure of Strapi Rich Text blocks
export type RichTextNode = {
  type: string;
  text?: string;
  children?: RichTextNode[];
  format?: string;
  level?: number;
  url?: string;
  image?: {
    url: string;
    width: number;
    height: number;
    alternativeText?: string;
    caption?: string;
  };
  [key: string]: unknown;
};

interface RichTextRendererProps {
  content: RichTextNode[];
}

// Client-side image component with error handling
const RichTextImage = ({ src, alt, width, height }: { src: string; alt: string; width: number; height: number }) => {
  const [imgSrc, setImgSrc] = useState(src);
  const [isError, setIsError] = useState(false);
  
  const handleError = () => {
    console.error(`Rich text image failed to load: ${src}`);
    setIsError(true);
    // Fallback to placeholder
    setImgSrc('/images/blog-placeholder.jpg');
  };
  
  return (
    <Image
      src={imgSrc}
      alt={alt}
      width={width || 800}
      height={height || 600}
      className={`max-h-[600px] object-contain ${isError ? 'bg-gray-900 p-2' : ''}`}
      onError={handleError}
    />
  );
};

const RichTextRenderer: React.FC<RichTextRendererProps> = ({ content }) => {
  if (!content || !Array.isArray(content)) {
    return <p>No content available</p>;
  }

  // Render a single node
  const renderNode = (node: RichTextNode, index: number) => {
    const { type, children } = node;

    // Text node
    if (type === 'text') {
      const text = node.text || '';
      
      // Apply formatting if needed
      if (node.bold) {
        return <strong key={index} className="text-white">{text}</strong>;
      }
      if (node.italic) {
        return <em key={index} className="text-gray-200">{text}</em>;
      }
      if (node.underline) {
        return <u key={index} className="text-gray-200">{text}</u>;
      }
      if (node.code) {
        return <code key={index} className="bg-gray-800 text-blue-300 rounded px-1 py-0.5">{text}</code>;
      }
      
      return <span key={index} className="text-gray-200">{text}</span>;
    }

    // Paragraph
    if (type === 'paragraph') {
      return (
        <p key={index} className="mb-4 text-gray-200">
          {children?.map((child, i) => renderNode(child, i))}
        </p>
      );
    }

    // Headings
    if (type === 'heading') {
      const level = node.level || 1;
      switch (level) {
        case 1:
          return <h1 key={index} className="text-3xl font-bold mt-8 mb-4 text-white">{children?.map((child, i) => renderNode(child, i))}</h1>;
        case 2:
          return <h2 key={index} className="text-2xl font-bold mt-6 mb-3 text-white">{children?.map((child, i) => renderNode(child, i))}</h2>;
        case 3:
          return <h3 key={index} className="text-xl font-bold mt-5 mb-2 text-white">{children?.map((child, i) => renderNode(child, i))}</h3>;
        case 4:
          return <h4 key={index} className="text-lg font-bold mt-4 mb-2 text-white">{children?.map((child, i) => renderNode(child, i))}</h4>;
        case 5:
          return <h5 key={index} className="text-base font-bold mt-3 mb-1 text-white">{children?.map((child, i) => renderNode(child, i))}</h5>;
        case 6:
          return <h6 key={index} className="text-sm font-bold mt-3 mb-1 text-white">{children?.map((child, i) => renderNode(child, i))}</h6>;
        default:
          return <h3 key={index} className="text-xl font-bold mt-5 mb-2 text-white">{children?.map((child, i) => renderNode(child, i))}</h3>;
      }
    }

    // Lists
    if (type === 'list') {
      const format = node.format || 'unordered';
      if (format === 'ordered') {
        return (
          <ol key={index} className="list-decimal mb-4 pl-8 text-gray-200">
            {children?.map((child, i) => renderNode(child, i))}
          </ol>
        );
      }
      return (
        <ul key={index} className="list-disc mb-4 pl-8 text-gray-200">
          {children?.map((child, i) => renderNode(child, i))}
        </ul>
      );
    }

    if (type === 'list-item') {
      return (
        <li key={index} className="mb-1 text-gray-200">
          {children?.map((child, i) => renderNode(child, i))}
        </li>
      );
    }

    // Link
    if (type === 'link') {
      return (
        <Link 
          key={index} 
          href={node.url || '#'} 
          className="text-blue-400 hover:underline font-medium"
          target={node.url?.startsWith('http') ? '_blank' : undefined}
          rel={node.url?.startsWith('http') ? 'noopener noreferrer' : undefined}
        >
          {children?.map((child, i) => renderNode(child, i))}
        </Link>
      );
    }

    // Image
    if (type === 'image' && node.image) {
      const baseUrl = siteConfig.strapiUrl;
      const imageUrl = node.image.url.startsWith('http') 
        ? node.image.url 
        : `${baseUrl}${node.image.url}`;
        
      return (
        <figure key={index} className="my-8">
          <div className="relative flex justify-center w-full">
            <RichTextImage 
              src={imageUrl}
              alt={node.image.alternativeText || 'Blog image'}
              width={node.image.width || 800}
              height={node.image.height || 600}
            />
          </div>
          {node.image.caption && (
            <figcaption className="text-center text-sm text-gray-400 mt-2">
              {node.image.caption}
            </figcaption>
          )}
        </figure>
      );
    }

    // Blockquote
    if (type === 'quote' || type === 'blockquote') {
      return (
        <blockquote key={index} className="border-l-4 border-blue-500 pl-4 py-2 my-4 italic text-gray-300 bg-gray-900 rounded-sm">
          {children?.map((child, i) => renderNode(child, i))}
        </blockquote>
      );
    }

    // Code block
    if (type === 'code') {
      return (
        <pre key={index} className="bg-gray-900 p-4 rounded my-4 overflow-x-auto">
          <code className="text-blue-300">{children?.map((child, i) => renderNode(child, i))}</code>
        </pre>
      );
    }

    // Default fallback - render children if available
    if (children) {
      return <div key={index} className="text-gray-200">{children.map((child, i) => renderNode(child, i))}</div>;
    }

    // Complete fallback
    return <span key={index} className="text-gray-200">{JSON.stringify(node)}</span>;
  };

  return <div className="rich-text-content">{content.map((node, i) => renderNode(node, i))}</div>;
};

export default RichTextRenderer; 